/**
 * Utilitaires pour détecter et gérer les boucles d'authentification Keycloak
 * causées par des problèmes de cookies tiers
 */

// Stockage des tentatives de redirection dans sessionStorage
const REDIRECT_ATTEMPTS_KEY = 'keycloak_redirect_attempts';
const MAX_REDIRECT_ATTEMPTS = 2; // Autoriser 5 redirections au lieu de 1
const REDIRECT_WINDOW_MS = 40000; // 60 secondes au lieu de 30

/**
 * Enregistre une tentative de redirection Keycloak
 */
export const recordKeycloakRedirectAttempt = () => {
    // console.log('📝 [KeycloakLoop] Enregistrement d\'une tentative de redirection');
    
    try {
        const now = Date.now();
        const existingAttempts = getRedirectAttempts();
        
        // Filtrer les tentatives récentes (dans la fenêtre de temps)
        const recentAttempts = existingAttempts.filter(
            attempt => (now - attempt.timestamp) < REDIRECT_WINDOW_MS
        );
        
        // Ajouter la nouvelle tentative
        const newAttempt = {
            timestamp: now,
            url: window.location.href,
            userAgent: navigator.userAgent.substring(0, 100) // Limiter la taille
        };
        
        recentAttempts.push(newAttempt);
        
        // console.log(`📝 [KeycloakLoop] ${recentAttempts.length} tentatives dans les ${REDIRECT_WINDOW_MS}ms`, recentAttempts);
        
        sessionStorage.setItem(REDIRECT_ATTEMPTS_KEY, JSON.stringify(recentAttempts));
        
        return recentAttempts.length;
    } catch (error) {
        console.error('📝 [KeycloakLoop] Erreur enregistrement tentative:', error);
        return 0;
    }
};

/**
 * Récupère les tentatives de redirection enregistrées
 */
export const getRedirectAttempts = () => {
    try {
        const stored = sessionStorage.getItem(REDIRECT_ATTEMPTS_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (error) {
        console.error('📝 [KeycloakLoop] Erreur lecture tentatives:', error);
        return [];
    }
};

/**
 * Vérifie si on est dans une boucle de redirection Keycloak
 */
export const detectKeycloakLoop = () => {
    // console.log('🔄 [KeycloakLoop] Détection de boucle');
    
    const attempts = getRedirectAttempts();
    const now = Date.now();
    
    // Filtrer les tentatives récentes
    const recentAttempts = attempts.filter(
        attempt => (now - attempt.timestamp) < REDIRECT_WINDOW_MS
    );
    
    // Logique plus intelligente pour détecter une vraie boucle
    let isInLoop = false;
    let reason = '';
    
    if (recentAttempts.length >= MAX_REDIRECT_ATTEMPTS) {
        // Vérifier si c'est vraiment une boucle problématique
        const rapidAttempts = recentAttempts.filter(
            attempt => (now - attempt.timestamp) < 10000 // 10 secondes
        );
        
        // Si plus de 3 tentatives en 10 secondes, c'est probablement une vraie boucle
        if (rapidAttempts.length >= 3) {
            isInLoop = true;
            reason = 'Trop de redirections rapides (probablement cookies bloqués)';
        } else {
            // Sinon, c'est probablement des rafraîchissements normaux
            reason = 'Redirections espacées (rafraîchissement normal Keycloak)';
        }
    }
    
    // console.log(`🔄 [KeycloakLoop] ${recentAttempts.length}/${MAX_REDIRECT_ATTEMPTS} tentatives récentes`);
    
    if (isInLoop) {
        console.warn('⚠️ [KeycloakLoop] BOUCLE DÉTECTÉE! ' + reason);
        // console.log('🔄 [KeycloakLoop] Détails des tentatives:', recentAttempts);
    } else if (recentAttempts.length > 0) {
        console.info('ℹ️ [KeycloakLoop] ' + reason);
    }
    
    return {
        isInLoop,
        attemptCount: recentAttempts.length,
        maxAttempts: MAX_REDIRECT_ATTEMPTS,
        recentAttempts,
        windowMs: REDIRECT_WINDOW_MS,
        reason
    };
};

/**
 * Réinitialise le compteur de tentatives de redirection
 */
export const clearRedirectAttempts = () => {
    // console.log('🗑️ [KeycloakLoop] Nettoyage des tentatives de redirection');
    
    try {
        sessionStorage.removeItem(REDIRECT_ATTEMPTS_KEY);
    } catch (error) {
        console.error('🗑️ [KeycloakLoop] Erreur nettoyage:', error);
    }
};

/**
 * Analyse l'URL pour détecter des paramètres Keycloak suspects
 */
export const analyzeKeycloakUrl = (url = window.location.href) => {
    // console.log(`🔍 [KeycloakLoop] Analyse URL: ${url}`);
    
    const urlObj = new URL(url);
    const searchParams = urlObj.searchParams;
    
    const analysis = {
        hasKeycloakParams: false,
        hasErrorParams: false,
        hasStateParam: false,
        hasCodeParam: false,
        hasSessionStateParam: false,
        keycloakParams: {},
        errorDetails: null
    };
    
    // Paramètres Keycloak typiques
    const keycloakParamNames = [
        'state',
        'session_state', 
        'code',
        'error',
        'error_description',
        'error_uri'
    ];
    
    keycloakParamNames.forEach(paramName => {
        if (searchParams.has(paramName)) {
            analysis.hasKeycloakParams = true;
            analysis.keycloakParams[paramName] = searchParams.get(paramName);
            
            switch (paramName) {
                case 'state':
                    analysis.hasStateParam = true;
                    break;
                case 'session_state':
                    analysis.hasSessionStateParam = true;
                    break;
                case 'code':
                    analysis.hasCodeParam = true;
                    break;
                case 'error':
                    analysis.hasErrorParams = true;
                    analysis.errorDetails = {
                        error: searchParams.get('error'),
                        description: searchParams.get('error_description'),
                        uri: searchParams.get('error_uri')
                    };
                    break;
            }
        }
    });
    
    // console.log('🔍 [KeycloakLoop] Résultat analyse URL:', analysis);
    
    return analysis;
};

/**
 * Monitore les changements d'URL pour détecter les redirections Keycloak
 */
export const startKeycloakLoopMonitoring = () => {
    // console.log('👀 [KeycloakLoop] Démarrage du monitoring');
    
    let lastUrl = window.location.href;
    
    // Analyser l'URL initiale
    const initialAnalysis = analyzeKeycloakUrl();
    if (initialAnalysis.hasKeycloakParams) {
        recordKeycloakRedirectAttempt();
    }
    
    // Surveiller les changements d'URL (pour les SPAs)
    const checkUrlChange = () => {
        const currentUrl = window.location.href;
        
        if (currentUrl !== lastUrl) {
            // console.log(`👀 [KeycloakLoop] Changement URL détecté: ${lastUrl} → ${currentUrl}`);
            
            const analysis = analyzeKeycloakUrl(currentUrl);
            
            if (analysis.hasKeycloakParams) {
                const attemptCount = recordKeycloakRedirectAttempt();
                
                // Vérifier si on entre dans une boucle
                const loopStatus = detectKeycloakLoop();
                if (loopStatus.isInLoop) {
                    console.error('🚨 [KeycloakLoop] BOUCLE INFINIE DÉTECTÉE!');
                    
                    // Déclencher un événement personnalisé pour alerter l'application
                    window.dispatchEvent(new CustomEvent('keycloakLoopDetected', {
                        detail: {
                            loopStatus,
                            analysis,
                            currentUrl
                        }
                    }));
                }
            }
            
            lastUrl = currentUrl;
        }
    };
    
    // Vérifier toutes les 500ms
    const intervalId = setInterval(checkUrlChange, 500);
    
    // Surveiller aussi les événements de navigation
    const handlePopState = () => {
        // console.log('👀 [KeycloakLoop] Navigation popstate détectée');
        setTimeout(checkUrlChange, 100);
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
        // console.log('👀 [KeycloakLoop] Arrêt du monitoring');
        clearInterval(intervalId);
        window.removeEventListener('popstate', handlePopState);
    };
};

/**
 * Fonction d'urgence pour sortir d'une boucle Keycloak
 */
export const breakKeycloakLoop = () => {
    console.warn('🚨 [KeycloakLoop] TENTATIVE DE SORTIE DE BOUCLE');
    
    try {
        // Nettoyer tous les cookies liés à Keycloak
        document.cookie.split(";").forEach(cookie => {
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            const cleanName = name.trim();
            
            if (cleanName.toLowerCase().includes('keycloak') || 
                cleanName.includes('KC_') ||
                cleanName.includes('AUTH_SESSION_ID')) {
                
                // console.log(`🚨 [KeycloakLoop] Suppression cookie: ${cleanName}`);
                document.cookie = `${cleanName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
                document.cookie = `${cleanName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
            }
        });
        
        // Nettoyer le localStorage et sessionStorage
        ['localStorage', 'sessionStorage'].forEach(storageType => {
            const storage = window[storageType];
            const keysToRemove = [];
            
            for (let i = 0; i < storage.length; i++) {
                const key = storage.key(i);
                if (key && (key.toLowerCase().includes('keycloak') || 
                           key.toLowerCase().includes('auth') ||
                           key.toLowerCase().includes('oidc'))) {
                    keysToRemove.push(key);
                }
            }
            
            keysToRemove.forEach(key => {
                // console.log(`🚨 [KeycloakLoop] Suppression ${storageType}: ${key}`);
                storage.removeItem(key);
            });
        });
        
        // Nettoyer l'historique des redirections
        clearRedirectAttempts();
        
        // Rediriger vers la page d'accueil sans paramètres
        // console.log('🚨 [KeycloakLoop] Redirection vers la page d\'accueil');
        window.location.href = window.location.origin;
        
    } catch (error) {
        console.error('🚨 [KeycloakLoop] Erreur lors de la sortie de boucle:', error);
        
        // En dernier recours, recharger la page
        window.location.reload();
    }
};